import {
  Home as HomeIcon,
  Hammer,
  LayoutGrid,
  Bath,
  Layers,
  Wrench,
  Droplets,
  Square,
  ArrowUpRight,
} from "lucide-react";
import { BRAND } from "@/config/brand";
import { TESTIDS } from "@/constants/testIds";

const SERVICE_MEDIA = {
  Siding: {
    icon: HomeIcon,
    image:
      "https://images.unsplash.com/photo-1706808849777-96e0d7be3bb7?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  },
  Roofing: {
    icon: Hammer,
    image: BRAND.photos[1], // real roof hip detail
  },
  "Ceramic Floors": {
    icon: LayoutGrid,
    image:
      "https://images.unsplash.com/photo-1706629503586-2731f65587ae?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  },
  "Kitchen & Baths": {
    icon: Bath,
    image:
      "https://images.unsplash.com/photo-1665507279638-5b48073c637b?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  },
  "Cement Work": {
    icon: Layers,
    image:
      "https://images.unsplash.com/photo-1583517190311-846a7653d8be?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  },
  Coping: {
    icon: Wrench,
    image: BRAND.photos[2], // real Vasquez roof craftsmanship
  },
  "Power Washing": {
    icon: Droplets,
    image: BRAND.photos[0], // real Vasquez crew photo (outdoor jobsite)
  },
  Doormats: {
    icon: Square,
    image:
      "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  },
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid={TESTIDS.services.root}
      className="relative bg-[color:var(--vs-bg)] py-24 md:py-32 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[color:var(--vs-blue)]" />
              <span className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[color:var(--vs-blue)]">
                001 / Services
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight">
              <span className="chrome-text">What</span> we
              <br />
              <span className="text-[color:var(--vs-blue)]">Build.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-gray-400 text-base md:text-lg leading-relaxed">
              From the studs to the shingles — every surface, every finish. One
              crew, one accountable team. Over two decades transforming Long
              Island homes.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {BRAND.services.map((s, i) => {
            const media = SERVICE_MEDIA[s.key] || SERVICE_MEDIA.Siding;
            const Icon = media.icon;
            return (
              <a
                key={s.key}
                href="#contact"
                data-testid={`${TESTIDS.services.card}-${i}`}
                className="group relative overflow-hidden bg-[#0a0a0b] border border-white/10 hover:border-[color:var(--vs-blue)]/80 transition-colors duration-300 flex flex-col"
              >
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={media.image}
                    alt={`${s.key} — Vasquez & Son Construction`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 flex items-center justify-center bg-black/70 backdrop-blur-sm border border-[color:var(--vs-blue)]/60">
                    <Icon
                      className="w-5 h-5 text-[color:var(--vs-blue)] transition-transform duration-500 group-hover:scale-110"
                      strokeWidth={1.75}
                    />
                  </div>
                  <div className="absolute top-4 right-4 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/70">
                    0{i + 1}
                  </div>
                  <ArrowUpRight className="absolute bottom-4 right-4 w-5 h-5 text-white/60 group-hover:text-[color:var(--vs-blue)] transition-all group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>

                {/* Text */}
                <div className="p-6 md:p-7 flex-1 flex flex-col justify-between gap-3">
                  <h3 className="font-display uppercase text-xl md:text-2xl text-white font-semibold tracking-tight leading-tight">
                    {s.key}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
